import { useTranslation } from "@/hooks";
import { useSidebar, useUIStore } from "@/stores/ui";
import { CloseButton, Flex, Paper, Text } from "@mantine/core";
import React, { useEffect, useRef, useState } from "react";
import { useWorkspaceDataflowId } from "../context/WorkspaceContext";
import "./WorkspaceSidebar.css";
import { WorkspaceSidebarDiscover } from "./discover/WorkspaceSidebarDiscover";
import { WorkspaceSidebarFlow } from "./flow/WorkspaceSidebarFlow";
import { WorkspaceSidebarLayers } from "./layers";

const SIDEBAR_ANIMATION_DELAY = 300;

export function WorkspaceSidebar() {
  const dataflowId = useWorkspaceDataflowId();
  const sidebar = useSidebar();
  const closeSidebar = useUIStore((state) => state.closeSidebar);
  const { t } = useTranslation();
  const [displayContent, setDisplayContent] = useState(sidebar.mode);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    if (sidebar.mode) {
      setDisplayContent(sidebar.mode);
    } else if (displayContent) {
      timeoutRef.current = setTimeout(() => {
        setDisplayContent(null);
      }, SIDEBAR_ANIMATION_DELAY);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [sidebar.mode, t, displayContent]);

  const content = (): { title: string; Component: React.ComponentType } => {
    switch (displayContent) {
      case "discover":
        return { title: "Datasets", Component: WorkspaceSidebarDiscover };
      case "flow":
        return { title: "Flow", Component: WorkspaceSidebarFlow };
      case "layers":
        return { title: "Layers", Component: WorkspaceSidebarLayers };
      default:
        return { title: "", Component: React.Fragment };
    }
  };

  const Content = content();

  const sidebarClass = sidebar.mode
    ? "workspace-sidebar-open"
    : "workspace-sidebar-closed";

  return (
    <div className={`workspace-sidebar ${sidebarClass}`}>
      <Paper className="workspace-sidebar-paper" shadow="xl" p={0}>
        <Flex direction="column" h="100%">
          <Flex
            justify="space-between"
            align="center"
            p="md"
            pb={0}
            className="workspace-sidebar-header"
          >
            <Text fz={16} fw={500} c="dark">
              {Content.title}
            </Text>
            <CloseButton
              bg="0"
              color="gray.9"
              size="sm"
              onClick={() => closeSidebar(dataflowId)}
            />
          </Flex>
          <Flex direction="column" className="workspace-sidebar-content" p={0}>
            <Content.Component />
          </Flex>
        </Flex>
      </Paper>
    </div>
  );
}
